import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP,
} from '@heidi/design-system/typography';
import { type ScreenName } from '@heidi/runtime/analytics-vocabulary';

import { OnboardingStepShell } from '../shared/OnboardingStepShell';
import { tourStepsMessages } from './tour-steps.messages';
import { useTourScreenView } from './useTourScreenView';

interface TourStepShellProps {
  /** Screen identity for the once-per-mount `screen_viewed` event. */
  screenName: ScreenName;
  title: MessageDescriptor;
  subtitle: MessageDescriptor;
  /** The screen's single control (detail slider, document picker, questions). */
  children: React.ReactNode;
  onContinue: () => void;
  onSkip: () => void;
  /** Blocks Continue until the screen's task has been performed. */
  continueDisabled?: boolean;
  /**
   * Why Continue is blocked, shown beside it while it is. Without this the
   * screen asks for an action it never names and a dead button is the only
   * feedback — the tour tasks are the one place in the flow where the block is
   * an activity rather than an empty field.
   */
  continueHint?: MessageDescriptor;
}

/**
 * Common frame for the three guided-tour screens: heading, one control, and a
 * footer that stays reachable via `OnboardingStepShell`.
 */
export function TourStepShell({
  screenName,
  title,
  subtitle,
  children,
  onContinue,
  onSkip,
  continueDisabled,
  continueHint,
}: TourStepShellProps) {
  useTourScreenView(screenName);

  return (
    <OnboardingStepShell
      contentClassName="gap-6"
      footer={
        // The design's "Button Row" is a right-aligned pair at their natural
        // widths, matching the sibling tour screens (invite, privacy) — not a
        // stack of full-width buttons. `flex-wrap` covers a long translation
        // in the 400px column.
        <div className="flex flex-wrap items-center justify-end gap-2">
          {continueDisabled && continueHint && (
            <TypographyCaption className="me-auto text-text-secondary" role="status">
              <FormattedMessage {...continueHint} />
            </TypographyCaption>
          )}
          <Button type="button" size="lg" variant="ghost" onClick={onSkip}>
            <FormattedMessage {...tourStepsMessages.skip} />
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={onContinue}
            disabled={continueDisabled}
          >
            <FormattedMessage {...tourStepsMessages.continue} />
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        <TypographyH3>
          <FormattedMessage {...title} />
        </TypographyH3>
        <TypographyP className="text-text-secondary">
          <FormattedMessage {...subtitle} />
        </TypographyP>
      </div>
      {children}
    </OnboardingStepShell>
  );
}
